import { DocsComponentDemo } from "../DocsComponentDemo";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Target,
  MessageSquare,
  Users,
  RefreshCw,
  Zap,
  Shield,
  Info
} from "lucide-react";
import { mockWorkstyleScores } from "../mockData";
import { cn } from "@/lib/utils";

const DIMENSION_INFO = {
  conscientiousness: { 
    label: "Structure Preferences", 
    description: "Preference for defined processes versus flexibility and autonomy",
    icon: Target,
  },
  communication: { 
    label: "Communication", 
    description: "Clear expression, aligns expectations, adapts to audience",
    icon: MessageSquare,
  },
  collaboration: { 
    label: "Collaboration", 
    description: "Works well with others, supportive team player",
    icon: Users,
  },
  adaptability: { 
    label: "Adaptability", 
    description: "Handles change well, learns quickly, flexible approach",
    icon: RefreshCw,
  },
  ownership: { 
    label: "Ownership", 
    description: "Takes initiative, accountable for outcomes, self-driven",
    icon: Zap,
  },
  resilience: { 
    label: "Resilience", 
    description: "Stays effective under pressure, persistent, bounces back",
    icon: Shield,
  },
};

function getScoreColor(score: number) {
  if (score >= 80) return "text-green-600";
  if (score >= 60) return "text-blue-600";
  if (score >= 40) return "text-amber-600";
  return "text-red-600";
}

function getScoreLabel(score: number) {
  if (score >= 80) return "High";
  if (score >= 60) return "Good";
  if (score >= 40) return "Moderate";
  return "Low";
}

export function DemoWorkStyleDimensions() {
  const scores = mockWorkstyleScores;
  const overallFitScore = Math.round(
    Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length
  );

  return (
    <DocsComponentDemo 
      caption="Work Style Assessment showing 6 behavioral dimensions with hover tooltips"
      fullWidth
    >
      <TooltipProvider>
        <div className="space-y-4">
          {/* Overall Score */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-full bg-primary/10">
                <Target className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm font-medium">Overall Fit Score</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn("text-lg font-bold", getScoreColor(overallFitScore))}>
                {overallFitScore}%
              </span>
              <Badge variant="secondary" className={cn(getScoreColor(overallFitScore))}>
                {getScoreLabel(overallFitScore)}
              </Badge>
            </div>
          </div>

          {/* Dimension Bars */}
          <div className="grid gap-3">
            {Object.entries(scores).map(([key, score]) => {
              const dimension = DIMENSION_INFO[key as keyof typeof DIMENSION_INFO];
              const Icon = dimension.icon;
              
              return (
                <div key={key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{dimension.label}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground/50 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-xs">{dimension.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <span className={cn("text-sm font-medium", getScoreColor(score))}>
                      {score}%
                    </span>
                  </div>
                  <Progress value={score} className="h-2" />
                </div>
              );
            })}
          </div>
        </div>
      </TooltipProvider>
    </DocsComponentDemo>
  );
}
